import { useEffect, useRef, useState } from "react";

const AD_CLIENT = "ca-pub-6025261484028438";

/**
 * No shared manual slot: the account runs on Auto Ads, so placements only
 * render an <ins> when an explicit, active slot ID is passed in.
 */
const DEFAULT_SLOT: string | null = null;

interface AdSenseProps {
  slot?: string;
  format?: "auto" | "horizontal" | "vertical" | "rectangle" | "fluid";
  layoutKey?: string;
  responsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

/**
 * Renders a Google AdSense unit. Falls back to the shared responsive display
 * slot so every placement is an explicit, reservable ad unit rather than
 * relying on Auto Ads to guess. Auto Ads still runs alongside these.
 */
const AdSense = ({
  slot,
  format = "auto",
  layoutKey,
  responsive = true,
  className = "",
  style,
}: AdSenseProps) => {
  const adSlot = slot ?? DEFAULT_SLOT;
  const pushed = useRef(false);
  const insRef = useRef<HTMLModElement>(null);
  const [filled, setFilled] = useState<boolean | null>(null);

  useEffect(() => {
    if (!adSlot) return;
    if (pushed.current) return;
    if (typeof window === "undefined") return;
    pushed.current = true;
    // The loader script is injected after first paint, so retry briefly until
    // the adsbygoogle queue exists.
    let attempts = 0;
    const tryPush = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        if (attempts++ < 10) setTimeout(tryPush, 500);
      }
    };
    tryPush();
  }, [adSlot]);

  // Collapse the placeholder when Google reports the slot as unfilled so we
  // don't leave large blank gaps in the layout.
  useEffect(() => {
    const el = insRef.current;
    if (!el) return;
    const check = () => {
      const status = el.getAttribute("data-ad-status");
      if (status === "unfilled") setFilled(false);
      else if (status === "filled") setFilled(true);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(el, { attributes: true, attributeFilter: ["data-ad-status"] });
    // Safety net: if nothing renders within 6s, collapse the space.
    const timer = setTimeout(() => {
      if (!el.getAttribute("data-ad-status") && !el.firstChild) setFilled(false);
    }, 6000);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  if (!adSlot || filled === false) return null;

  return (
    <div className={`adsense-container ${className}`}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={style ?? { display: "block" }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={adSlot}
        data-ad-format={format}
        {...(layoutKey ? { "data-ad-layout-key": layoutKey } : {})}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
    </div>
  );

};

// A single shared display unit is used everywhere, so all placements request
// the responsive "auto" format — "fluid" only works with native in-feed /
// in-article units, which would render blank against a display slot.
export const InFeedAd = ({ className = "", slot }: { className?: string; slot?: string }) => (
  <AdSense slot={slot} format="auto" className={`my-8 ${className}`} />
);

export const InArticleAd = ({ className = "", slot }: { className?: string; slot?: string }) => (
  <AdSense slot={slot} format="auto" className={`my-6 ${className}`} />
);

export const SidebarAd = ({ className = "", slot }: { className?: string; slot?: string }) => (
  <AdSense slot={slot} format="auto" className={`mb-6 ${className}`} />
);

export const FooterAd = ({ className = "", slot }: { className?: string; slot?: string }) => (
  <AdSense slot={slot} format="auto" className={className} />
);

export default AdSense;
